import React, { useEffect, useState } from 'react';
import { Alert, Button, Stack, TextField } from '@mui/material';
import { SiteProjectService } from 'services/SiteProjectService';

export default function UsbSiteCodeControl({
  projectId,
  busy,
  onReady,
}: {
  projectId: number;
  busy: boolean;
  onReady: (ready: boolean) => void;
}) {
  const [code, setCode] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const valid = /^[a-z0-9][a-z0-9-]{0,30}$/.test(code);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    onReady(false);
    SiteProjectService.getProject(projectId)
      .then((site) => {
        if (cancelled) return;
        setCode(site.site_code || '');
        setSaved(site.site_code || '');
        setError('');
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '현장 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, retry, onReady]);
  useEffect(() => {
    onReady(!loading && !saving && valid && code === saved);
  }, [loading, saving, valid, code, saved, onReady]);
  return (
    <Stack spacing={1}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          label="설치 현장 코드"
          value={code}
          size="small"
          placeholder="예: osong"
          disabled={busy || loading || saving}
          onChange={(e) => setCode(e.target.value.trim())}
          error={!loading && !valid}
          helperText="영문 소문자·숫자·하이픈 1~31자. 현장 이름·PC 호스트명과 별도로 저장합니다."
        />
        <Button
          disabled={busy || loading || saving || !valid || code === saved}
          onClick={async () => {
            setSaving(true);
            setError('');
            try {
              const site = await SiteProjectService.updateProject(projectId, { site_code: code });
              setSaved(site.site_code || '');
            } catch (e) {
              setError(e instanceof Error ? e.message : '현장 코드 저장 실패');
            } finally {
              setSaving(false);
            }
          }}
        >
          현장 코드 저장
        </Button>
      </Stack>
      {!loading && (!valid || code !== saved) && (
        <Alert severity="info">
          번들을 만들기 전에 유효한 현장 코드를 저장하세요. 오송 현장 코드가 osong이라면 osong을
          입력하세요.
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          action={<Button onClick={() => setRetry((value) => value + 1)}>다시 조회</Button>}
        >
          {error}
        </Alert>
      )}
    </Stack>
  );
}
